import React, { useState } from 'react';
import { Descope } from '@descope/react-sdk';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { logger } from '../utils/logger';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [showDevBypass, setShowDevBypass] = useState(false);
  
  // Get redirect URL from query params
  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  // If already authenticated, redirect to dashboard
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectUrl);
    }
  }, [isAuthenticated, navigate, redirectUrl]);

  // Handle successful authentication
  const onSuccess = (e: any) => {
    // Descope handles token storage automatically
    logger.info('User authenticated successfully', e);
    navigate(redirectUrl);
  };

  // Handle authentication flow error
  const onError = (e: any) => {
    logger.error('Authentication error:', e);
    setError('Authentication error. Please try again.');
    setShowDevBypass(true);
  };

  // Development bypass for authentication
  const handleDevBypass = (role: 'candidate' | 'interviewer') => {
    // Store role in session storage for AuthContext to pick up
    sessionStorage.setItem('user_role', role);
    
    // Mock user session
    const mockToken = 'dev-token-' + Math.random().toString(36).substring(2);
    sessionStorage.setItem('descope.session', JSON.stringify({
      token: mockToken,
      refreshExpiry: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    }));

    // Simulate loading and redirect
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 1000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
              start your 14-day free trial
            </a>
          </p>
        </div>
        
        {/* Descope Authentication Component */}
        <Descope
          flowId="sign-up-or-in"
          onSuccess={onSuccess}
          onError={onError}
        />

        {/* Development Bypass (only shown in development) */}
        {showDevBypass && process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-4 border border-gray-200 rounded-md">
            <h3 className="text-sm font-medium text-gray-700">Development Bypass</h3>
            <div className="mt-2 space-x-4">
              <button
                onClick={() => handleDevBypass('interviewer')}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Login as Interviewer
              </button>
              <button
                onClick={() => handleDevBypass('candidate')}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                Login as Candidate
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login; 